import React, { useEffect, useRef } from 'react';

import TabelaPaginada, { ColunaTabela } from './TabelaPaginada';
import './modal-close-button.css'; // X padrão do sistema

/**
 * Modal de detalhamento genérico dos dashboards (at-86).
 *
 * ⚠️ Irmão do `DetalhePlacasModal`, e a diferença é o que ele detalha: aquele lista ATENDIMENTOS
 * (a linha é uma placa); este lista qualquer coisa — as esteiras onde um colaborador trabalhou, as
 * passagens de uma esteira. Forçar tudo pelo modal de placas transformaria "horas por esteira" numa
 * lista de veículos, que é outra pergunta.
 *
 * Fecha por clique fora e por Escape; recebe `sidebarRef` porque o fechamento exige clique fora do
 * modal E fora da sidebar — sem a ref, o caminho fica inerte.
 */

interface AbaModal {
  id: string;
  rotulo: string;
}

interface Props<T> {
  titulo: string;
  /**
   * Abas opcionais dentro do modal.
   *
   * ⚠️ Quem troca de aba espera ver SÓ aquele grupo — a filtragem dos itens é de quem chama, e é
   * proposital: só a tela sabe o que separa um grupo do outro.
   */
  abas?: AbaModal[];
  abaAtiva?: string;
  onTrocarAba?: (id: string) => void;
  /** Frase curta acima da tabela, explicando o que está sendo listado. */
  apoio?: string;
  /**
   * Bloco livre acima da tabela, para quando uma frase não basta (comissionamentos/at-04: o
   * comparativo "fotografia × hoje", e os estados em que não há o que comparar).
   *
   * ⚠️ Opcional e aditivo de propósito — nenhum chamador existente muda. Existe para que uma
   * tela com conteúdo próprio acima da tabela possa continuar usando ESTE modal, em vez de
   * recriar casco, cabeçalho, abas, fechamento e tabela do zero, que foi o que a at-04 fez na
   * primeira versão.
   */
  cabecalho?: React.ReactNode;
  itens: T[];
  colunas: Array<ColunaTabela<T>>;
  chave: (item: T, indice: number) => string;
  vazio: string;
  /** Clique na linha — usado para descer um nível no detalhamento. */
  onClickLinha?: (item: T) => void;
  onFechar: () => void;
  sidebarRef?: React.RefObject<HTMLElement>;
}

function DetalheTabelaModal<T>({
  titulo,
  abas,
  abaAtiva,
  onTrocarAba,
  apoio,
  cabecalho,
  itens,
  colunas,
  chave,
  vazio,
  onClickLinha,
  onFechar,
  sidebarRef,
}: Props<T>) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const aoClicarFora = (evento: MouseEvent) => {
      const alvo = evento.target as Node;
      if (ref.current && !ref.current.contains(alvo) && !sidebarRef?.current?.contains(alvo)) {
        onFechar();
      }
    };
    const aoTeclar = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') onFechar();
    };
    document.addEventListener('mousedown', aoClicarFora);
    document.addEventListener('keydown', aoTeclar);
    return () => {
      document.removeEventListener('mousedown', aoClicarFora);
      document.removeEventListener('keydown', aoTeclar);
    };
  }, [onFechar, sidebarRef]);

  return (
    <div className="modal-overlay">
      <div className="do-modal" ref={ref} role="dialog" aria-modal="true" aria-label={titulo}>
        <div className="do-modal-header">
          <h2 className="do-modal-titulo" title={titulo}>
            {titulo}
          </h2>
          <button type="button" className="close-button" onClick={onFechar} aria-label="Fechar">
            &times;
          </button>
        </div>

        <div className="do-modal-corpo">
          {cabecalho}
          {abas && abas.length > 0 && (
            <div className="recepcao-tabs" style={{ marginBottom: 12 }}>
              {abas.map((aba) => (
                <button
                  key={aba.id}
                  type="button"
                  className={`recepcao-tab ${abaAtiva === aba.id ? 'active' : ''}`}
                  onClick={() => onTrocarAba?.(aba.id)}
                >
                  {aba.rotulo}
                </button>
              ))}
            </div>
          )}
          {apoio && <p className="do-modal-apoio">{apoio}</p>}
          <TabelaPaginada
            itens={itens}
            colunas={colunas}
            chave={chave}
            vazio={vazio}
            onClickLinha={onClickLinha}
            porPagina={10}
          />
        </div>
      </div>
    </div>
  );
}

export default DetalheTabelaModal;
